import type {
	GameHistoryLogPayloadResponse,
	GameHistoryLogSnapshotResponse,
} from "libs/apiClient";
import { _ } from "i18n";
import { GameHistoryStatusBadge } from "./GameHistoryStatusBadge";

const FIELD_LABEL_KEYS = {
	clubManagerId: "discover.gameDetail.history.log.field.clubManagerId",
	contentManagerId: "discover.gameDetail.history.log.field.contentManagerId",
	assigneeId: "discover.gameDetail.history.log.field.assigneeId",
} as const;

type UserField = keyof typeof FIELD_LABEL_KEYS;

/** Call at render time: `_()` must not run at module load (translations load async in main.tsx). */
function emptyValueLabel(): string {
	return _("discover.gameDetail.history.log.emptyValue");
}

function isUserField(field: string): field is UserField {
	return Object.hasOwn(FIELD_LABEL_KEYS, field);
}

function formatUserName(snapshot: GameHistoryLogSnapshotResponse): string {
	const fullName = snapshot.fullName;
	if (typeof fullName === "string" && fullName.trim() !== "") {
		return fullName;
	}
	const email = snapshot.email;
	if (typeof email === "string" && email.trim() !== "") {
		return email;
	}
	const userId = snapshot.userId;
	if (userId !== null && userId !== undefined && String(userId) !== "0") {
		return String(userId);
	}
	return _("discover.gameDetail.history.log.unassigned");
}

function formatScalarValue(snapshot: GameHistoryLogSnapshotResponse): string {
	const value = snapshot.value;
	if (value === null || value === undefined || value === "") {
		return emptyValueLabel();
	}
	return String(value);
}

interface GameHistoryLogChangeProps {
	log: GameHistoryLogPayloadResponse;
	gameStatusLabels: Record<string, string>;
}

export function GameHistoryLogChange({ log, gameStatusLabels }: GameHistoryLogChangeProps) {
	const oldSnapshot = log.old;
	const newSnapshot = log.new;

	if (log.field === "gameStatus") {
		const oldRaw = oldSnapshot.value;
		const newRaw = newSnapshot.value;
		const oldValue = oldRaw !== undefined && oldRaw !== null && oldRaw !== "" ? oldRaw : null;
		const newValue = newRaw !== undefined && newRaw !== null && newRaw !== "" ? newRaw : null;
		return (
			<>
				{_("discover.gameDetail.history.log.changedStatus")}{" "}
				<GameHistoryStatusBadge
					statusValue={oldValue}
					label={oldValue ? (gameStatusLabels[oldValue] ?? oldValue) : emptyValueLabel()}
				/>{" "}
				{_("discover.gameDetail.history.log.to")}{" "}
				<GameHistoryStatusBadge
					statusValue={newValue}
					label={newValue ? (gameStatusLabels[newValue] ?? newValue) : emptyValueLabel()}
				/>
			</>
		);
	}

	if (log.field === "priority") {
		return (
			<>
				{_("discover.gameDetail.history.log.changedPriority")}{" "}
				<strong>{formatScalarValue(oldSnapshot)}</strong>{" "}
				{_("discover.gameDetail.history.log.to")}{" "}
				<strong>{formatScalarValue(newSnapshot)}</strong>
			</>
		);
	}

	if (isUserField(log.field)) {
		const fieldKey = FIELD_LABEL_KEYS[log.field];
		const fieldLabel = _(fieldKey);
		return (
			<>
				{_("discover.gameDetail.history.log.changedUserField", { field: fieldLabel })}{" "}
				<strong>{formatUserName(oldSnapshot)}</strong>{" "}
				{_("discover.gameDetail.history.log.to")}{" "}
				<strong>{formatUserName(newSnapshot)}</strong>
			</>
		);
	}

	return (
		<>
			{log.field}: <strong>{formatScalarValue(oldSnapshot)}</strong>{" "}
			{_("discover.gameDetail.history.log.to")}{" "}
			<strong>{formatScalarValue(newSnapshot)}</strong>
		</>
	);
}
